/**
 * Customer Reviews Page
 *
 * Displays pending reviews and submitted reviews for customers:
 * - Pending reviews (trips awaiting feedback)
 * - Submitted reviews with edit capability
 * - Review history
 *
 * Epic: MVP 1.21.4 - Customer Dashboard
 * Story: US-1.21.4-09: Pending Reviews List (3 SP)
 * Story: US-1.21.4-11: View My Reviews (3 SP)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/customer/reviews/pending
 * - Fetches from GET /api/v1/customer/reviews
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  Star as _Star,
  Calendar,
  MapPin as _MapPin,
  Edit,
  Clock,
  AlertCircle as _AlertCircle,
  CheckCircle,
  MessageSquare
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import EmptyState from '../../components/common/EmptyState';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import Avatar from '../../components/common/Avatar';
import { logError } from '../../services/errorLoggingService';

export default function CustomerReviews() {
  const navigate = useNavigate();
  const [pendingReviews, setPendingReviews] = useState([]);
  const [submittedReviews, setSubmittedReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // pending, submitted

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const [pendingResponse, submittedResponse] = await Promise.all([
        apiRequest('/api/v1/customer/reviews/pending', { method: 'GET' }),
        apiRequest('/api/v1/customer/reviews', { method: 'GET' })
      ]);

      if (pendingResponse.success) {
        setPendingReviews(pendingResponse.data);
      }
      if (submittedResponse.success) {
        setSubmittedReviews(submittedResponse.data);
      }
    } catch (err) {
      logError(err, { componentName: 'CustomerReviews', action: 'fetch_reviews' });
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load reviews');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (dateString) => {
    const tripDate = new Date(dateString);
    const expiryDate = new Date(tripDate.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days after trip
    const now = new Date();
    const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysLeft);
  };

  const canEditReview = (reviewDate) => {
    const reviewTime = new Date(reviewDate);
    const now = new Date();
    const daysSinceReview = Math.floor((now - reviewTime) / (1000 * 60 * 60 * 24));
    return daysSinceReview <= 7;
  };

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole="customer" activeNavId="reviews" onNavigate={handleNavigate}>
        <LoadingSpinner message="Loading reviews..." className="min-h-screen" />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole="customer" activeNavId="reviews" onNavigate={handleNavigate}>
        <ErrorAlert message={error} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="customer"
      activeNavId="reviews"
      onNavigate={handleNavigate}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
          <p className="mt-1 text-sm text-gray-500">
            Share your feedback and help other adventurers
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" role="tablist" aria-label="Review categories">
            <button
              onClick={() => setActiveTab('pending')}
              role="tab"
              aria-selected={activeTab === 'pending'}
              aria-controls="pending-panel"
              id="pending-tab"
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                activeTab === 'pending'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pending Reviews
              {pendingReviews.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {pendingReviews.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('submitted')}
              role="tab"
              aria-selected={activeTab === 'submitted'}
              aria-controls="submitted-panel"
              id="submitted-tab"
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                activeTab === 'submitted'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Reviews
              {submittedReviews.length > 0 && (
                <span className="ml-2 text-gray-500">({submittedReviews.length})</span>
              )}
            </button>
          </nav>
        </div>

        {/* Pending Reviews Tab */}
        {activeTab === 'pending' && (
          <div
            className="space-y-4"
            role="tabpanel"
            id="pending-panel"
            aria-labelledby="pending-tab"
          >
            {pendingReviews.length === 0 ? (
              <EmptyState
                icon={CheckCircle}
                iconClassName="w-16 h-16 text-green-500 mx-auto mb-4"
                title="All Caught Up!"
                description="You have no pending reviews at this time."
              />
            ) : (
              pendingReviews.map((trip) => {
                const daysRemaining = getDaysRemaining(trip.start_date);
                return (
                  <Card
                    key={trip.booking_id}
                  >
                    <div className="flex gap-6">
                      {/* Trip Image */}
                      <div className="flex-shrink-0">
                        {trip.experience_image ? (
                          <img
                            src={trip.experience_image}
                            alt={trip.experience_title}
                            className="w-24 h-24 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                            <Calendar className="w-10 h-10 text-white" />
                          </div>
                        )}
                      </div>

                      {/* Trip Details */}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {trip.experience_title}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          with {trip.guide_name}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDate(trip.start_date)}
                          </span>
                          <span className="flex items-center">
                            <_MapPin className="w-4 h-4 mr-1" />
                            {trip.location}
                          </span>
                        </div>

                        {/* Time Remaining Warning */}
                        {daysRemaining <= 7 && (
                          <div className="mt-3 flex items-center space-x-2 p-2 bg-orange-50 border border-orange-200 rounded-md">
                            <Clock className="w-4 h-4 text-orange-600 flex-shrink-0" />
                            <p className="text-sm text-orange-800">
                              <strong>{daysRemaining} days</strong> remaining to submit review
                            </p>
                          </div>
                        )}

                        {/* Action Button */}
                        <div className="mt-4">
                          <Button
                            onClick={() => navigate(`/customer/reviews/new?booking=${trip.booking_id}`)}
                          >
                            <_Star className="w-4 h-4" />
                            <span>Write Review</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Submitted Reviews Tab */}
        {activeTab === 'submitted' && (
          <div
            className="space-y-4"
            role="tabpanel"
            id="submitted-panel"
            aria-labelledby="submitted-tab"
          >
            {submittedReviews.length === 0 ? (
              <EmptyState
                icon={MessageSquare}
                title="No Reviews Yet"
                description="Your submitted reviews will appear here."
              />
            ) : (
              submittedReviews.map((review) => {
                const canEdit = canEditReview(review.created_at);
                return (
                  <Card
                    key={review.review_id}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {review.experience_title}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Guide: {review.guide_name}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            Trip: {formatDate(review.start_date)}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            Reviewed: {formatDate(review.created_at)}
                          </span>
                        </div>
                      </div>
                      {canEdit && (
                        <button
                          onClick={() => navigate(`/customer/reviews/edit/${review.review_id}`)}
                          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                      )}
                    </div>

                    {/* Rating */}
                    <div className="flex items-center space-x-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <_Star
                          key={star}
                          className={`w-5 h-5 ${
                            star <= review.overall_rating
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        {review.overall_rating.toFixed(1)}
                      </span>
                    </div>

                    {/* Review Text */}
                    {review.review_text && (
                      <p className="text-gray-700 leading-relaxed mb-3">
                        {review.review_text}
                      </p>
                    )}

                    {/* Category Ratings */}
                    {review.category_ratings && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-200">
                        {Object.entries(review.category_ratings).map(([category, rating]) => (
                          <div key={category}>
                            <p className="text-xs text-gray-600 capitalize">
                              {category.replace('_', ' ')}
                            </p>
                            <div className="flex items-center space-x-1 mt-1">
                              <_Star className="w-3 h-3 text-yellow-400 fill-current" />
                              <span className="text-sm font-medium text-gray-900">{rating}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Guide Response */}
                    {review.guide_response && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <Avatar
                            name={review.guide_name}
                            size="sm"
                            fallbackBg="bg-blue-600"
                            className="flex-shrink-0"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900">
                              Response from {review.guide_name}
                            </p>
                            <p className="text-sm text-gray-700 mt-1">
                              {review.guide_response}
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                              {formatDate(review.guide_response_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Edit Window Notice */}
                    {!canEdit && (
                      <div className="mt-4 text-xs text-gray-500">
                        Review editing window expired (7 days after submission)
                      </div>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
